import { getContentDependencyErrorMessage } from "@/components/content-dependency/DependenciesList";
import {
  EventSourceMessageError,
  EventSourceRequestError,
  useEventSource,
} from "@/hooks/use-event-source";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import { useCallback, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  type GetMessagesResponseDto,
  groupKitChatControllerDeleteMessage,
  groupKitChatControllerGetMessages,
} from "../lib/gen-api";

type MessageDto = GetMessagesResponseDto["data"][number];
import {
  ConversationCompleteEventDto,
  MessageChunkEventDto,
  MessageCreatedEventDto,
  MessageUpdatedEventDto,
  PostMessageBodyDto,
  ToolApprovalRequiredEventDto,
  groupKitChatControllerResolveToolApproval,
} from "../lib/gen-brain";
import { queryKeys } from "../lib/query-keys";

// Use shared types from backend
type MessageStreamEvent =
  | MessageCreatedEventDto
  | MessageChunkEventDto
  | MessageUpdatedEventDto
  | ConversationCompleteEventDto
  | ToolApprovalRequiredEventDto;

export type PendingToolApproval = {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  chatUuid: string;
};

export type ExtendedMessageDto = MessageDto & {
  isStreaming?: boolean;
};

type CreateMessageParams = PostMessageBodyDto & {
  overwriteChatUuid?: string;
  overwriteKitUuid?: string;
  onInitiated?: () => void;
  onError?: (error: Error) => void;
};

export type MessageStage = "initializing" | "streaming" | "idle";

const CREATE_MESSAGE_ERROR_FALLBACK =
  "Failed to create message. Please try again later.";

function getCreateMessageErrorToast(error: unknown): string {
  const listed = getContentDependencyErrorMessage(error);
  if (listed) {
    return listed;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return CREATE_MESSAGE_ERROR_FALLBACK;
}

function shouldCaptureChatStreamError(error: Error): boolean {
  return (
    error.name !== "AbortError" &&
    (!(error instanceof EventSourceRequestError) || error.status >= 500) &&
    !(error instanceof EventSourceMessageError)
  );
}

function toExtendedMessageDto(message: MessageDto): ExtendedMessageDto {
  return {
    ...message,
    isStreaming: false,
  };
}

// ============================================================
// Streaming Chats Store
// ============================================================

/** Reactive module-level store for tracking which chats are currently streaming.
 *  Survives component re-mounts (e.g. navigation to a new chat). */
const streamingChatsListeners = new Set<() => void>();
const streamingChats = new Map<string, MessageStage>();
// A new chat can remount the input while its request is still streaming.
const streamAbortHandlers = new Map<string, () => void>();

const streamingChatsStore = {
  initialize: (chatUuid: string) => {
    streamingChats.set(chatUuid, "initializing");
    streamingChatsListeners.forEach((l) => l());
  },
  streaming: (chatUuid: string) => {
    if (streamingChats.has(chatUuid)) {
      streamingChats.set(chatUuid, "streaming");
      streamingChatsListeners.forEach((l) => l());
    }
  },
  delete: (chatUuid: string) => {
    streamingChats.delete(chatUuid);
    streamingChatsListeners.forEach((l) => l());
  },
  has: (chatUuid: string) => streamingChats.has(chatUuid),
  subscribe: (listener: () => void) => {
    streamingChatsListeners.add(listener);
    return () => streamingChatsListeners.delete(listener);
  },
};

// ============================================================
// Pending Tool Approval Store
// ============================================================

const pendingApprovalListeners = new Set<() => void>();
let pendingToolApproval: PendingToolApproval | null = null;

const pendingToolApprovalStore = {
  set: (approval: PendingToolApproval) => {
    pendingToolApproval = approval;
    pendingApprovalListeners.forEach((listener) => listener());
  },
  clear: () => {
    pendingToolApproval = null;
    pendingApprovalListeners.forEach((listener) => listener());
  },
  getForChat: (chatUuid: string) =>
    pendingToolApproval?.chatUuid === chatUuid ? pendingToolApproval : null,
  subscribe: (listener: () => void) => {
    pendingApprovalListeners.add(listener);
    return () => pendingApprovalListeners.delete(listener);
  },
};

function usePendingToolApproval(chatUuid?: string | null) {
  return useSyncExternalStore(
    pendingToolApprovalStore.subscribe,
    () => (chatUuid ? pendingToolApprovalStore.getForChat(chatUuid) : null),
    () => null,
  );
}

export const useMessagesData = (
  groupUuid?: string | null,
  kitUuid?: string | null,
  chatUuid?: string | null,
) => {
  const queryClient = useQueryClient();
  const { request, abort } = useEventSource<
    MessageStreamEvent,
    PostMessageBodyDto
  >({
    method: "POST",
    basePath: `/brain/api/v1/groups/${groupUuid}/kits`,
  });

  const queryKey = queryKeys.chat(chatUuid!);
  const createMessageStage: MessageStage = useSyncExternalStore(
    streamingChatsStore.subscribe,
    () => streamingChats.get(chatUuid!) || "idle",
    () => "idle",
  );
  const pendingToolApproval = usePendingToolApproval(chatUuid);

  const { data, error, refetch } = useQuery<ExtendedMessageDto[]>({
    enabled:
      useIsApiReady() &&
      !!groupUuid &&
      !!kitUuid &&
      !!chatUuid &&
      !streamingChats.has(chatUuid),
    queryKey,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!groupUuid || !kitUuid || !chatUuid) {
        return [];
      }

      const messages = await groupKitChatControllerGetMessages({
        path: {
          chatUuid: chatUuid!,
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => res.data?.data ?? []);

      return messages.map(toExtendedMessageDto);
    },
  });

  const isMessageStreamEvent = (data: unknown): data is MessageStreamEvent => {
    return (
      !!data &&
      typeof data === "object" &&
      "type" in data &&
      typeof data.type === "string" &&
      [
        "message_created",
        "message_chunk",
        "message_updated",
        "message_reasoning",
        "conversation_complete",
        "tool_approval_required",
      ].includes(data.type)
    );
  };

  const handleMessageStreamEvent = useCallback(
    (event: MessageStreamEvent, chatUuid: string) => {
      if (event.type === "tool_approval_required") {
        pendingToolApprovalStore.set({
          toolCallId: event.toolCallId,
          toolName: event.toolName,
          args: event.args,
          chatUuid: event.chatUuid,
        });
        return;
      }

      queryClient.setQueryData(
        queryKeys.chat(chatUuid),
        (oldMessages: ExtendedMessageDto[] | undefined) => {
          const newMessages = !oldMessages ? [] : [...oldMessages];

          switch (event.type) {
            case "message_created": {
              // Add new message to the array
              const newMessage: ExtendedMessageDto = {
                uuid: event.message.uuid,
                content: event.message.content,
                type: event.message.type,
                dateCreated: event.message.dateCreated,
                order: newMessages.length + 1, // Assign next order
                chat: {
                  uuid: event.message.chat.uuid,
                  expertUuid: event.message.chat.expertUuid,
                  isSummary: event.message.chat.isSummary,
                },
                isStreaming:
                  // AI messages will be streaming
                  event.message.type === "model",
              };
              newMessages.push(newMessage);
              break;
            }

            case "message_chunk": {
              // Find the message and append the chunk
              const chunkIndex = newMessages.findIndex(
                (msg) => msg.uuid === event.messageId,
              );
              if (chunkIndex !== -1) {
                newMessages[chunkIndex] = {
                  ...newMessages[chunkIndex],
                  content: newMessages[chunkIndex].content + event.chunk,
                  isStreaming: true,
                };
              }
              break;
            }

            case "message_updated": {
              // Replace the message with the complete version
              const updateIndex = newMessages.findIndex(
                (msg) => msg.uuid === event.message.uuid,
              );
              if (updateIndex !== -1) {
                newMessages[updateIndex] = {
                  uuid: event.message.uuid,
                  content: event.message.content,
                  type: event.message.type,
                  dateCreated: event.message.dateCreated,
                  order: newMessages[updateIndex].order,
                  chat: {
                    uuid: event.message.chat.uuid,
                    expertUuid: event.message.chat.expertUuid,
                    isSummary: event.message.chat.isSummary,
                  },
                  isStreaming: false,
                };
              }
              break;
            }

            case "conversation_complete": {
              pendingToolApprovalStore.clear();
              return newMessages.map((msg) => {
                if (msg.chat.uuid !== event.conversationId) {
                  return msg;
                }
                return {
                  ...msg,
                  isStreaming: false,
                };
              });
            }
          }

          return newMessages;
        },
      );
    },
    [queryClient],
  );

  const submitToolApproval = useCallback(
    async (params: {
      decision: "approve" | "deny";
      note?: string;
      targetChatUuid?: string;
      targetKitUuid?: string;
    }) => {
      const approval = pendingToolApprovalStore.getForChat(
        params.targetChatUuid || chatUuid || "",
      );

      if (!groupUuid || !approval) {
        throw new Error("No pending tool approval");
      }

      const localKitUuid = params.targetKitUuid || kitUuid;
      const localChatUuid = params.targetChatUuid || chatUuid;

      if (!localKitUuid || !localChatUuid) {
        throw new Error("Chat or kit uuid is missing");
      }

      const { error } = await groupKitChatControllerResolveToolApproval({
        path: {
          groupUuid: groupUuid!,
          kitUuid: localKitUuid,
          chatUuid: localChatUuid,
        },
        body: {
          toolCallId: approval.toolCallId,
          decision: params.decision,
          note: params.note,
        },
      });

      if (error) {
        throw new Error("Failed to submit tool approval");
      }

      pendingToolApprovalStore.clear();
    },
    [groupUuid, kitUuid, chatUuid],
  );

  const { mutateAsync: createMessage } = useMutation({
    mutationFn: ({
      prompt,
      overwriteChatUuid,
      skillUuid,
      overwriteKitUuid,
      documentUuid,
      capabilities,
      onInitiated,
      fileUuids,
      onError,
      modelProfileUuid,
    }: CreateMessageParams) => {
      const localChatUuid = overwriteChatUuid || chatUuid!;
      const localKitUuid = overwriteKitUuid || kitUuid!;
      const isImageGeneration = capabilities?.imageGeneration;

      if (!localChatUuid || !localKitUuid) {
        throw new Error("Chat or kit uuid is missing");
      }

      streamingChatsStore.initialize(localChatUuid);
      streamAbortHandlers.set(localChatUuid, abort);

      return request({
        path: `/${localKitUuid}/chats/${localChatUuid}/messages`,
        body: {
          prompt,
          skillUuid,
          documentUuid,
          capabilities,
          fileUuids,
          modelProfileUuid,
        },

        onMessage: (data) => {
          streamingChatsStore.streaming(localChatUuid);
          if (
            typeof data === "object" &&
            data.type === "tool_approval_required"
          ) {
            handleMessageStreamEvent(
              data as ToolApprovalRequiredEventDto,
              localChatUuid,
            );
            return;
          }

          // Handle new MessageStreamEvent format
          if (isMessageStreamEvent(data)) {
            handleMessageStreamEvent(data, localChatUuid);

            // Trigger onInitiated for message_created events
            if (data.type === "message_created" && onInitiated) {
              onInitiated();
            }
            return;
          }
        },
        onClose: () => {
          streamingChatsStore.delete(localChatUuid);
          setTimeout(() => {
            // Once the messages are done, the title of the chat has possibly changed.
            queryClient.invalidateQueries({
              queryKey: queryKeys.kitChats(groupUuid!, localKitUuid!, null),
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.kitChat(
                groupUuid!,
                localKitUuid!,
                localChatUuid,
              ),
            });

            // Image might be generated
            if (isImageGeneration) {
              queryClient.invalidateQueries({
                queryKey: queryKeys.files({
                  groupUuid: groupUuid!,
                  kitUuid: localKitUuid!,
                  isLibrary: true,
                }),
              });
            }
            refetch();
          }, 1000);
          streamingChatsStore.delete(localChatUuid);
        },
        onError: (error) => {
          streamingChatsStore.delete(localChatUuid);
          pendingToolApprovalStore.clear();
          if (shouldCaptureChatStreamError(error)) {
            captureException(error, {
              tags: { source: "chat_stream_browser" },
              contexts: {
                chat: {
                  groupUuid,
                  kitUuid: localKitUuid,
                  chatUuid: localChatUuid,
                },
              },
            });
          }
          toast.error(getCreateMessageErrorToast(error));
          onError?.(error);
        },
      }).finally(() => {
        if (streamAbortHandlers.get(localChatUuid) === abort) {
          streamAbortHandlers.delete(localChatUuid);
        }
      });
    },
  });

  const { mutateAsync: deleteMessage } = useMutation({
    mutationFn: async (messageUuid: string) => {
      if (!groupUuid || !kitUuid || !chatUuid) {
        throw new Error("Group, kit, or chat UUID is missing");
      }

      return groupKitChatControllerDeleteMessage({
        path: {
          groupUuid,
          kitUuid,
          chatUuid,
          messageUuid,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat(chatUuid!),
      });
    },
    onError: () => {
      toast.error("Failed to delete message");
    },
  });

  const cancelMessage = useCallback(() => {
    streamingChatsStore.delete(chatUuid!);
    streamAbortHandlers.get(chatUuid!)?.();

    const currentMessages = queryClient.getQueryData<ExtendedMessageDto[]>(
      queryKeys.chat(chatUuid!),
    );
    if (currentMessages) {
      const streamingMessage = currentMessages.find((msg) => msg.isStreaming);
      if (streamingMessage) {
        deleteMessage(streamingMessage.uuid);
      }
    }

    toast.info("Message was cancelled.");
  }, [chatUuid, queryClient, deleteMessage]);

  return {
    messages: data || [],
    error,
    createMessage,
    createMessageStage,
    deleteMessage,
    cancelMessage,
    submitToolApproval,
    pendingToolApproval,
  };
};
